import { AppState } from '../types/store';
import { getPage, getFeedTypeSubnavigation } from './page';
import { Pages } from '../../constants';
import {
  getIsCurrentStreamEmpty,
  getCurrentPage,
  getHasMore,
  getInitialOffset,
  getPageSize,
  getCaughtUpIndex,
} from './stream';
import { getPageUrl } from '../helpers/getPageUrl';
import {
  getPagingMode,
  getIsMobile,
} from '@wix/da-shared-react/pkg/publicSession';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';

/** Should the PageContainer for this facet/browse-page show the site footer? */
export function getShouldShowFooter(state: AppState): boolean {
  const page = getPage(state);

  if (page === Pages.DailyDeviation) {
    return true;
  }

  if (!getIsMobile(state) && getPagingMode(state) !== 'scroll') {
    return true;
  }

  if (page === Pages.Search) {
    // Show footer in case search page has no results
    return getIsCurrentStreamEmpty(state);
  }

  return false;
}

interface RelatedSeoNavigation {
  next?: string;
  prev?: string;
}
/** Creates a pair of urls for use in link rel="next/prev" and SEO
 *  metadata relative to the current content
 *  https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types */
export function getRelatedSeoNavigation(state: AppState): RelatedSeoNavigation {
  let currentPage = getCurrentPage(state);
  currentPage = typeof currentPage !== 'undefined' ? currentPage : 0;
  const hasMore = getHasMore(state);
  const requestUrl = state.environment.requestUrl;

  return {
    next: hasMore ? getPageUrl(currentPage + 1, requestUrl) : undefined,
    prev: currentPage > 0 ? getPageUrl(currentPage - 1, requestUrl) : undefined,
  };
}

/** Gets the page a given offset would fall on based on page size */
export function getPageForOffset(state: AppState, offset: number): number {
  const initialOffset = getInitialOffset(state);
  const pageSize = getPageSize(state);
  return Math.floor((offset + initialOffset) / pageSize) + 1;
}

export function getShowNetworkBarInSiteHeader(state: AppState) {
  if (!getIsNetworkBarPage(state)) {
    return true;
  }

  return ['search', 'tag', 'topic'].includes(
    state.currentPageData?.pageType ?? 'home'
  );
}

export function getSelectedNetworkBarItem(state: AppState) {
  if (state.currentPageData?.pageType === 'groups') {
    return state.currentPageData?.thisGroup;
  }

  return state.currentPageData?.pageType;
}

export function getIsSelectedNetworkBarItemRead(state: AppState) {
  const nav = getFeedTypeSubnavigation(state);
  if (flagEnabled(state, 'new_content_separator')) {
    const caughtUpIndex = getCaughtUpIndex(state);
    return nav?.options?.every(option => {
      if (option.subnav === nav?.currentValue) {
        // on the current item, we're caught up if the index is somewhere on the current page
        return !caughtUpIndex || caughtUpIndex < Number.POSITIVE_INFINITY;
      }

      return !option.hasNewContent;
    });
  }
  return nav?.options?.every(
    option => option.subnav === nav?.currentValue || !option.hasNewContent
  );
}

// because we need to branch not just based on feature, but per-page as well,
// use this instead of the usual flag checks...
export function getIsNetworkBarPage(state: AppState) {
  // we rely on the fact that faceted pages do not init this part of the store
  return !!state.currentPageData;
}
